import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor, useSlideEditor } from '@/providers/editor/EditorProvider';
import {
    Coordinate,
    DndResult,
    isDndResult,
    isSlideComponentDnd,
    PropsWithPreview,
    Question,
    Size,
    SlideComponent,
    SlideComponentDndDragZone,
    SlideComponentDndDropZone,
    SlideComponentDnd as SlideComponentDndModel,
} from '@/types/slide';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import BorderClearIcon from '@mui/icons-material/BorderClear';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { SlideRef } from '../Slide';
import { DndSettingsPopover } from './DndSettingsPopover';
import { SlideComponentDndDragObject } from './SlideComponentDndDragObject';
import { SlideComponentDndDropObject } from './SlideComponentDndDropObject';

const COLOR_ARRAY = ['#F44336', '#FF9800', '#FDD835', '#AEEA00', '#4CAF50', '#03A9F4', '#304FFE', '#E040FB', '#FF80AB'];

const DISABLE_SETTINGS = true;

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
    question?: Question;
}

export const SlideComponentDnd: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef, question }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { selectedSlide } = useSlideEditor();
    const { deleteComponent, updateComponent, createSlideImage, bringToFront, bringToBack, undo, redo } = useComponentEditor();

    const dndSettingsRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize] = useState<Size>(model.size);
    const [tPos] = useState<Coordinate>(model.pos);

    const isEditing = isFocus || isSelected;

    const [dndSettingsAnchorEl, setDndSettingsAnchorEl] = useState<HTMLElement | null>(null);

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onUploadImage = React.useCallback(
        async (file: File): Promise<string> => {
            try {
                return await createSlideImage(selectedSlide.editorData?.id ?? '', file);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
                return '';
            }
        },
        [createSlideImage, notifications, selectedSlide.editorData?.id],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    if (!isSlideComponentDnd(model)) {
        return null;
    }

    const onCreateDragObject = (component: SlideComponentDndDragZone) => {
        const updatedContent = { ...model.content, dragZone: [...model.content.dragZone, component] };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        debouncedUpdateComponent?.(model.id, { ...updateModel });
    };

    const onUpdateDragObject = (id: string, component: SlideComponentDndDragZone) => {
        const updatedContent = { ...model.content, dragZone: model.content.dragZone.map((zone) => (zone.id === id ? component : zone)) };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        debouncedUpdateComponent?.(model.id, { ...updateModel });
    };

    const onDeleteDragObject = (id: string) => {
        if (model.content.dragZone.length <= 1) {
            notifications.show(t('slide.components.dnd.message.error.dragObjectLessThanOne'), { severity: 'error', autoHideDuration: 5000 });
            return;
        }

        // When deleting a drag object, we need to remove its ID from any drop zones that are connected to it
        const dropZonesWithConnections = model.content.dropZone.map((dropZone) => ({
            ...dropZone,
            connectIds: dropZone.connectIds.filter((connectId) => connectId !== id),
        }));

        const updatedContent = { ...model.content, dragZone: model.content.dragZone.filter((zone) => zone.id !== id), dropZone: dropZonesWithConnections };
        const updateModel = { ...model, content: updatedContent };
        const newModel = updateDragDropConnection(updateModel);
        setModel({ ...newModel });
        debouncedUpdateComponent?.(model.id, { ...newModel });
    };

    const onUpdateDragTag = (dragModel: SlideComponentDndDragZone, tag: string) => {
        const newDragModel = { ...dragModel, tag };
        const updatedContent = { ...model.content, dragZone: model.content.dragZone.map((zone) => (zone.id === dragModel.id ? newDragModel : zone)) };
        const updateModel = { ...model, content: updatedContent };
        const newModel = updateDragDropConnection(updateModel);
        setModel({ ...newModel });
        debouncedUpdateComponent?.(model.id, { ...newModel });
    };

    const onCreateDropObject = (component: SlideComponentDndDropZone) => {
        const updatedContent = { ...model.content, dropZone: [...model.content.dropZone, component] };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        debouncedUpdateComponent?.(model.id, { ...updateModel });
    };

    const onUpdateDropObject = (id: string, component: SlideComponentDndDropZone) => {
        const updatedContent = { ...model.content, dropZone: model.content.dropZone.map((zone) => (zone.id === id ? component : zone)) };
        const updateModel = { ...model, content: updatedContent };
        setModel({ ...updateModel });
        debouncedUpdateComponent?.(model.id, { ...updateModel });
    };

    const onDeleteDropObject = (id: string) => {
        if (model.content.dropZone.length <= 1) {
            notifications.show(t('slide.components.dnd.message.error.dropObjectLessThanOne'), { severity: 'error', autoHideDuration: 5000 });
            return;
        }

        const updatedContent = { ...model.content, dropZone: model.content.dropZone.filter((zone) => zone.id !== id) };
        const updateModel = { ...model, content: updatedContent };
        const newModel = updateDragDropConnection(updateModel);
        setModel({ ...newModel });
        debouncedUpdateComponent?.(model.id, { ...newModel });
    };

    const onUpdateDropTag = (dropModel: SlideComponentDndDropZone, tag: string) => {
        const duplicatedTag = model.content.dropZone.find((zone) => zone.tag.toLowerCase() === tag.toLowerCase());
        let newDropModel = { ...dropModel };

        if (duplicatedTag) {
            newDropModel = { ...dropModel, tag: '' };
        } else {
            newDropModel = { ...dropModel, tag };
        }

        const updatedContent = { ...model.content, dropZone: model.content.dropZone.map((zone) => (zone.id === dropModel.id ? newDropModel : zone)) };
        const updateModel = { ...model, content: updatedContent };
        const newModel = updateDragDropConnection(updateModel);
        setModel({ ...newModel });
        debouncedUpdateComponent?.(model.id, { ...newModel });

        if (duplicatedTag) {
            throw new Error('NO_TAG');
        }
    };

    const updateDragDropConnection = (model: SlideComponentDndModel): SlideComponentDndModel => {
        const currentDragZones = model.content.dragZone || [];
        const currentDropZones = model.content.dropZone || [];

        const newDropZones = currentDropZones.map((dropZone) => {
            let newConnectIds: string[];

            if (!dropZone.tag || dropZone.tag.trim() === '') {
                // If dropZone has no tag or an empty tag, it should not connect to any dragZone
                newConnectIds = [];
            } else {
                // Find all dragZones that have the same tag as this dropZone (case-insensitive)
                newConnectIds = currentDragZones.filter((dragZone) => dragZone.tag && dragZone.tag.toLowerCase() === dropZone.tag.toLowerCase()).map((dragZone) => dragZone.id);
            }

            return { ...dropZone, connectIds: newConnectIds };
        });

        const updatedContent = { ...model.content, dropZone: newDropZones };
        const updatedModel = { ...model, content: updatedContent };

        return updatedModel;
    };

    const dragTagColors: { dragId: string; color: string | undefined }[] = [];
    const dropTagColors = model.content?.dropZone
        ?.filter((dropZone) => dropZone.tag !== '')
        .map((dropZone, index) => {
            const color = COLOR_ARRAY[index % COLOR_ARRAY.length];
            for (const connectedId of dropZone.connectIds) {
                dragTagColors.push({
                    dragId: connectedId,
                    color,
                });
            }
            return {
                dropId: dropZone.id,
                color,
            };
        });

    // display values
    let questionResult: DndResult | undefined = undefined;
    if (question) {
        const answer = question?.answers?.[0];
        const content = answer?.data ? JSON.parse(answer.data) : undefined;
        if (content?.dnd?.data) {
            content.dnd.data = content?.dnd?.data ? JSON.parse(content.dnd.data) : undefined;
        }
        questionResult = content && isDndResult(content) ? content : undefined;
    }

    return (
        <Box
            ref={ref}
            id={`dnd-component-${model.id}`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
                pointerEvents: 'none',
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
        >
            <ResizeBorder resizable={false} disabled size={tSize} pos={tPos} preview={preview} isSelected={isEditing} slideRef={slideRef}>
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#dnd-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.dnd.title')}
                    </ComponentLabel>
                )}

                {model.content?.dropZone?.map((dropZone) => (
                    <SlideComponentDndDropObject
                        key={dropZone.id}
                        init={{ focus: init?.focus }}
                        model={dropZone}
                        preview={preview}
                        onDeleteObject={onDeleteDropObject}
                        onUpdateObject={onUpdateDropObject}
                        onUploadImage={onUploadImage}
                        onBringToFrontHandler={() => onBringToFrontHandler(model.id)}
                        onBringToBackHandler={() => onBringToBackHandler(model.id)}
                        onUndo={undo}
                        onRedo={redo}
                        onUpdateTag={onUpdateDropTag}
                        tagColor={dropTagColors.find((color) => color.dropId === dropZone.id)?.color}
                        slideRef={slideRef}
                    />
                ))}

                {model.content?.dragZone?.map((dragZone) => (
                    <SlideComponentDndDragObject
                        key={dragZone.id}
                        init={{ focus: init?.focus }}
                        model={dragZone}
                        preview={preview}
                        onDeleteObject={onDeleteDragObject}
                        onUpdateObject={onUpdateDragObject}
                        onUploadImage={onUploadImage}
                        onBringToFrontHandler={() => onBringToFrontHandler(model.id)}
                        onBringToBackHandler={() => onBringToBackHandler(model.id)}
                        onUpdateTag={onUpdateDragTag}
                        onUndo={undo}
                        onRedo={redo}
                        tagColor={dragTagColors.find((color) => color.dragId === dragZone.id)?.color}
                        question={questionResult ? questionResult?.dnd.data.find((dnd) => dnd.content.id === dragZone.id)?.content || undefined : undefined}
                        slideRef={slideRef}
                    />
                ))}

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <AddBoxOutlinedIcon />,
                                tooltip: t('slide.components.dnd.tooltip.addDragObject'),
                                onClick: () => {
                                    onCreateDragObject({
                                        id: crypto.randomUUID(),
                                        type: 'default',
                                        pos: {
                                            x: 0,
                                            y: 0,
                                        },
                                        size: {
                                            width: 200,
                                            height: 200,
                                        },
                                        settings: {
                                            backgroundColor: '#DADEE7',
                                        },
                                        tag: '',
                                    });
                                },
                            },
                            {
                                icon: <BorderClearIcon />,
                                tooltip: t('slide.components.dnd.tooltip.addDropObject'),
                                onClick: () => {
                                    onCreateDropObject({
                                        id: crypto.randomUUID(),
                                        type: 'default',
                                        pos: {
                                            x: 0,
                                            y: 462,
                                        },
                                        size: {
                                            width: 200,
                                            height: 200,
                                        },
                                        settings: {
                                            backgroundColor: '#DADEE7',
                                            multiple: true,
                                        },
                                        tag: '',
                                        connectIds: [],
                                    });
                                },
                            },
                            ...(!DISABLE_SETTINGS
                                ? [
                                      {
                                          icon: <SettingsIcon />,
                                          tooltip: t('slide.components.dnd.tooltip.settings'),
                                          onClick: () => {
                                              setDndSettingsAnchorEl(ref.current);
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon sx={{ color: (theme) => (theme.vars || theme).palette.error.main }} />,
                                tooltip: t('slide.components.dnd.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}

                {dndSettingsAnchorEl && (
                    <DndSettingsPopover
                        anchorEl={dndSettingsAnchorEl}
                        slotProps={{
                            paper: {
                                ref: dndSettingsRef, // Attach the ref to the Paper element
                                sx: {
                                    p: 2,
                                },
                            },
                        }}
                        value={{
                            reusable: model?.content?.settings?.reusable,
                        }}
                        onClose={() => {
                            setDndSettingsAnchorEl(null);
                        }}
                        onUpdate={(settings) => {
                            const dndSettingsContent = {
                                reusable: settings.reusable,
                            };

                            const updatedContent = { ...model.content, settings: dndSettingsContent };
                            setModel({ ...model, content: updatedContent });
                            onUpdateComponent?.(model.id, { ...model, content: updatedContent });
                        }}
                    />
                )}
            </ResizeBorder>
        </Box>
    );
};
